import { type NavContentNavItems } from "@backstage/plugin-app-react";
import { Metadata } from "@internal/misc";
import {
    RiAccountCircleLine,
    RiAddCircleLine,
    RiArticleLine,
    RiBookOpenLine,
    RiBubbleChartLine,
    RiCheckDoubleLine,
    RiDashboard3Line,
    RiDatabase2Line,
    RiFireLine,
    RiHome4Line,
    RiKey2Line,
    RiMegaphoneLine,
    RiMoreLine,
    RiNotification3Line,
    RiSearchLine,
    RiToolsLine,
} from "@remixicon/react";

import { NAV_ICON_SIZE, SUBMENU_ICON_SIZE } from "../constants";
import { type SidebarEntry } from "../types";
import { AdminSidebar } from "./AdminSidebar";
import { kindIcons } from "./icons";
import { MobileSidebar } from "./MobileSidebar";
import { SidebarDivider } from "./SidebarDivider";
import { SidebarItem } from "./SidebarItem";
import { SidebarLogo } from "./SidebarLogo";
import styles from "./SidebarNav.module.css";
import { useSidebarOpen } from "./sidebarOpenState";
import { useSidebarPinState } from "./sidebarPinState";
import { SidebarRail } from "./SidebarRail";
import { SidebarSection } from "./SidebarSection";
import { SidebarSpacer } from "./SidebarSpacer";
import { SidebarSubmenu } from "./SidebarSubmenu";

export interface MainSidebarProps {
    navItems: NavContentNavItems;
}

/**
 * Entries in the Catalog submenu.
 *
 * The kind icons come from a local Remix map rather than
 * `useApp().getSystemIcon("kind:*")`, which still resolves to Material UI
 * icons — see `icons.tsx`.
 */
const catalogEntries: SidebarEntry[] = [
    {
        title: "Components",
        href: "/catalog?filters[kind]=component",
        icon: kindIcons.component,
    },
    {
        title: "APIs",
        href: "/catalog?filters[kind]=api",
        icon: kindIcons.api,
    },
    {
        title: "Templates",
        href: "/catalog?filters[kind]=template",
        icon: kindIcons.template,
    },
    {
        title: "Groups",
        href: "/catalog?filters[kind]=group",
        icon: kindIcons.group,
    },
    {
        title: "Users",
        href: "/catalog?filters[kind]=user",
        icon: kindIcons.user,
    },
    {
        title: "Constellation",
        href: "/constellation",
        icon: <RiBubbleChartLine size={SUBMENU_ICON_SIZE} aria-hidden />,
    },
];

/** Shortcuts pinned to the mobile bottom bar alongside the menu button. */
const mobileQuickLinks: SidebarEntry[] = [
    {
        title: "Home",
        href: "/",
        icon: <RiHome4Line size={NAV_ICON_SIZE} aria-hidden />,
    },
    {
        title: "Search",
        href: "/search",
        icon: <RiSearchLine size={NAV_ICON_SIZE} aria-hidden />,
    },
];

/**
 * Environment/build banner, hidden while the rail is collapsed.
 *
 * Rendered *below* the spacer, at the foot of the rail, rather than under the
 * logo where it started. Anything that appears only when the rail expands
 * pushes whatever follows it downwards, and from above the nav items that
 * means the item under the pointer slides out from under it at the exact
 * moment hovering triggers the expand. Below the spacer, the spacer absorbs
 * the height change and the items never move.
 *
 * Split out from {@link SidebarNav} so its `useSidebarOpen` call sits below
 * `SidebarRail`'s provider; `SidebarNav` renders above it and would always
 * read the collapsed default.
 */
const SidebarMetadata = () => {
    const isOpen = useSidebarOpen();

    if (!isOpen) {
        return null;
    }

    return (
        <div className={styles.metadata}>
            <Metadata />
        </div>
    );
};

/**
 * Banno's navigation, installed as the app's `nav-content` extension.
 *
 * Rebuilt on `@backstage/ui`: every Material UI component and icon the
 * previous implementation used is gone. Because BUI ships no navigation
 * primitives — it offers a top-bar `Header`, not a side rail — the rail and
 * its parts are local components in this package. See {@link SidebarRail},
 * {@link SidebarItem}, {@link SidebarSubmenu} and {@link MobileSidebar}.
 *
 * Destinations are declared statically rather than discovered. This sidebar is
 * a curated information architecture, not a listing of every installed plugin,
 * so `navItems.rest()` is deliberately never rendered.
 *
 * Home and Announcements are the exception: their paths come from the
 * discovered nav items, so they follow any route reconfiguration. Their icons
 * are *not* used — the discovered icon is whatever the owning plugin
 * registered, which for both is still a Material UI element, so a Remix
 * equivalent is substituted. This is also why the items are read with `take`
 * rather than rendered through `navItems.withComponent`.
 */
export const SidebarNav = ({ navItems }: MainSidebarProps) => {
    const { isMobile } = useSidebarPinState();

    const homeItem = navItems.take("page:home");
    const announcementsItem = navItems.take("page:announcements");

    const moreEntries: SidebarEntry[] = [
        ...(announcementsItem
            ? [
                  {
                      title: announcementsItem.title,
                      href: announcementsItem.href,
                      icon: (
                          <RiMegaphoneLine
                              size={SUBMENU_ICON_SIZE}
                              aria-hidden
                          />
                      ),
                  },
              ]
            : []),
        {
            title: "Entity Validator",
            href: "/entity-validation",
            icon: <RiToolsLine size={SUBMENU_ICON_SIZE} aria-hidden />,
        },
    ];

    const content = (
        <>
            {/*
             * Search sits in its own group above the main menu, matching the
             * original. It is a destination rather than one nav item among
             * many, and the dividers either side are what say so.
             */}
            <SidebarSection label="Search">
                <SidebarItem
                    icon={<RiSearchLine size={NAV_ICON_SIZE} aria-hidden />}
                    href="/search"
                    text="Search"
                />
            </SidebarSection>
            <SidebarDivider />
            <SidebarSection label="Menu">
                {homeItem && (
                    <SidebarItem
                        icon={<RiHome4Line size={NAV_ICON_SIZE} aria-hidden />}
                        href={homeItem.href}
                        text={homeItem.title}
                    />
                )}
                <SidebarSubmenu
                    icon={<RiBookOpenLine size={NAV_ICON_SIZE} aria-hidden />}
                    text="Catalog"
                    entries={catalogEntries}
                />
                <SidebarItem
                    icon={<RiArticleLine size={NAV_ICON_SIZE} aria-hidden />}
                    href="/docs"
                    text="Docs"
                />
                <SidebarItem
                    icon={
                        <RiCheckDoubleLine size={NAV_ICON_SIZE} aria-hidden />
                    }
                    href="/soundcheck"
                    text="Soundcheck"
                />
                <SidebarItem
                    icon={<RiAddCircleLine size={NAV_ICON_SIZE} aria-hidden />}
                    href="/create"
                    text="Templates"
                />
                <SidebarSubmenu
                    icon={<RiMoreLine size={NAV_ICON_SIZE} aria-hidden />}
                    text="More"
                    entries={moreEntries}
                />
            </SidebarSection>
            <SidebarDivider />
            <SidebarSection label="Tools">
                <SidebarItem
                    icon={<RiFireLine size={NAV_ICON_SIZE} aria-hidden />}
                    href="/firefighter"
                    text="Firefighter"
                />
                <SidebarItem
                    icon={<RiDatabase2Line size={NAV_ICON_SIZE} aria-hidden />}
                    href="/oneshotsql"
                    text="OneShotSQL"
                />
                <SidebarItem
                    icon={<RiKey2Line size={NAV_ICON_SIZE} aria-hidden />}
                    href="/secret-sharing"
                    text="Secret Sharing"
                />
                <SidebarItem
                    icon={<RiDashboard3Line size={NAV_ICON_SIZE} aria-hidden />}
                    href="/k8s-dashboard"
                    text="K8s Dashboard"
                />
            </SidebarSection>
            <SidebarSpacer />
            <SidebarMetadata />
            <SidebarDivider />
            <SidebarSection label="Settings">
                <SidebarItem
                    icon={
                        <RiNotification3Line size={NAV_ICON_SIZE} aria-hidden />
                    }
                    href="/notifications"
                    text="Notifications"
                />
                <SidebarItem
                    icon={
                        <RiAccountCircleLine size={NAV_ICON_SIZE} aria-hidden />
                    }
                    href="/settings"
                    text="Settings"
                />
                {/*
                 * Last within the section: it is the only expandable entry
                 * here, so keeping it at the bottom means opening it pushes
                 * nothing else around.
                 */}
                <AdminSidebar />
            </SidebarSection>
        </>
    );

    if (isMobile) {
        return (
            <MobileSidebar quickLinks={mobileQuickLinks}>
                {content}
            </MobileSidebar>
        );
    }

    return (
        <SidebarRail>
            <SidebarLogo />
            <SidebarDivider />
            {content}
        </SidebarRail>
    );
};
